import {
  BRAND_DOCUMENTS,
  DEFAULT_BRAND,
  DRIVE_QUERY_DELAY,
  CATCHE_QUERY_DELAY,
  INITIAL_USER,
  USER_PROFILE_PAGES,
  MATCHES_ORGANISATIONS_OPTIONS,
  USER_ACCESS_LEVEL
} from '@/utils/constants';
import { getCookie, COMMON_COOKIES_MAP } from 'ecx.saigon.ui.common';
import _isEqual from 'lodash/isEqual';
import _merge from 'lodash/merge';

/**
 * Determines if the users access level is within a given list of access levels
 * @param state
 * @returns {function(*): boolean}
 */
export function userIsAccessLevel(state) {
  return (accessLevels = []) => {
    if (!Array.isArray(accessLevels)) {
      return false;
    }
    return accessLevels.includes(state.user.accessLevel);
  };
}

/**
 * True if the user should not be able to access any pre life functionality.
 *
 * @param state
 * @returns {boolean}
 */
export function restrictPreLifeAccess(state) {
  // Dashboard access should always be restricted.
  if (userIsAccessLevel(state)([USER_ACCESS_LEVEL.DASHBOARD])) {
    return true;
  }

  return false;
}

/**
 * True if the user should be able to edit their profile
 *
 * @param state
 * @returns {boolean}
 */
export function canEditProfile(state) {
  return !userIsAccessLevel(state)([
    USER_ACCESS_LEVEL.CREDIT_ONLY,
    USER_ACCESS_LEVEL.DASHBOARD
  ]);
}

/**
 * Return a list of documents based on the brand entity of the logged in user
 */
export function getBrandDocuments(state) {
  if (BRAND_DOCUMENTS.hasOwnProperty(state.user.entity)) {
    return BRAND_DOCUMENTS[state.user.entity];
  }
  return BRAND_DOCUMENTS[DEFAULT_BRAND];
}

export function theme(state) {
  return () => {
    const brand = brandName(state)();
    const defaultBrand = _merge(
      {},
      state.ORGBranding?.defaultBrand,
      state.ORGBranding?.defaultBrand?.[brand]
    );
    const novatedBrand = _merge(
      {},
      state.ORGBranding?.novated,
      state.ORGBranding?.novated?.[brand]
    );
    return {
      defaultBrand,
      novatedBrand
    };
  };
}

export function brandName(state) {
  return () => {
    const cookieEntity = getCookie(COMMON_COOKIES_MAP.brandName.key);
    return state.user.entity || cookieEntity;
  };
}

/**
 * Returns a delay (ms) that is useful in some scenarios when querying ERPs
 * eg, multiple quote fetches
 */
export function entityQueryDelay(state) {
  return state.user.entity === 'fleetPartners'
    ? DRIVE_QUERY_DELAY
    : CATCHE_QUERY_DELAY;
}

/**
 * Determine if the user is different to the initial state.
 *
 * @param state
 * @returns {boolean}
 */
export function hasUser(state) {
  return !_isEqual(state.user, INITIAL_USER);
}

/**
 * Return the first profile stepper page that to pass the page validation
 */
export function profileSetupPage(state) {
  if (userRequiresCorporateEmail(state)) {
    return USER_PROFILE_PAGES.corporateEmail;
  }
  if (userRequiresCompanyName(state)) {
    return USER_PROFILE_PAGES.companyName;
  }
  if (userRequiresOrgSelection(state)) {
    return USER_PROFILE_PAGES.organisationSelection;
  }
  if (userRequiresPostcode(state)) {
    return USER_PROFILE_PAGES.postcode;
  }
  return USER_PROFILE_PAGES.contactNumber;
}

export function userHasMultipleOrgMatches(state) {
  return (
    state.user.profile.matchesOrganisations ===
    MATCHES_ORGANISATIONS_OPTIONS.multiple
  );
}

export function userHasNoOrgMatches(state) {
  return (
    !state.user.profile.matchesOrganisations ||
    state.user.profile.matchesOrganisations ===
      MATCHES_ORGANISATIONS_OPTIONS.none
  );
}

function userRequiresOrgSelection(state) {
  return !state.user.hasOrganisation && userHasMultipleOrgMatches(state);
}
function userRequiresCorporateEmail(state) {
  const profile = state.user.profile;

  // If the company email is being verified we will not pass this step
  if (profile.companyEmail && !profile.corporateEmailVerified) {
    return true;
  }

  return !profile.companyEmail && !profile.noCorporateEmail;
}
function userRequiresCompanyName(state) {
  return !state.user.profile.companyName && userHasNoOrgMatches(state);
}
function userRequiresPostcode(state) {
  return !state.user.profile.postcode;
}
function userRequiresContactNumber(state) {
  return !state.user.profile.contactNumber;
}

export function isProfileSetupComplete(state) {
  return profileSetupCompletePercentage(state) === 100;
}

export function userRequiresEmployerSetup(state) {
  return state.user.profile.companyName && userHasNoOrgMatches(state);
}

/**
 * Return the percentage of profile pages that have been complete
 */
export function profileSetupCompletePercentage(state) {
  let percentageIncrement = 100 / Object.keys(USER_PROFILE_PAGES).length;
  let percentageDone = 0;
  if (!userRequiresCorporateEmail(state)) {
    percentageDone += percentageIncrement;
  }
  if (!userRequiresCompanyName(state)) {
    percentageDone += percentageIncrement;
  }
  if (!userRequiresOrgSelection(state)) {
    percentageDone += percentageIncrement;
  }
  if (!userRequiresPostcode(state)) {
    percentageDone += percentageIncrement;
  }
  if (!userRequiresContactNumber(state)) {
    percentageDone += percentageIncrement;
  }
  return percentageDone;
}
